/**
 * Trigger a browser download for a given Blob.
 *
 * Creates a temporary object URL, appends an anchor to the document body,
 * clicks it, and then cleans up (removes the anchor and revokes the URL).
 *
 * @param {Blob} blob - The blob to download.
 * @param {string} filename - The suggested filename for the download.
 * @returns {void}
 */
export const downloadBlob = (blob, filename) => {
	if (!blob) {
		return;
	}

	const url = window.URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	link.remove();
	window.URL.revokeObjectURL(url);
};

/**
 * Extract a filename from a Response's `content-disposition` header, falling
 * back to a provided default when the header is missing or unparseable.
 *
 * Supports both `filename*=UTF-8''<encoded>` and plain `filename="<value>"`.
 *
 * @param {Response} response - The fetch Response whose headers to inspect.
 * @param {string} fallback - Filename to use when the header is absent.
 * @returns {string} The resolved filename.
 */
export const getFileName = (response, fallback) => {
	const disposition = response.headers.get('content-disposition');
	const utf8Match = disposition?.match(/filename\*=UTF-8''([^;]+)/i);
	const regularMatch = disposition?.match(/filename="?([^";]+)"?/i);
	const encodedName = utf8Match?.[1];

	if (encodedName) {
		try {
			return decodeURIComponent(encodedName);
		} catch (_) {
			return encodedName;
		}
	}

	return regularMatch?.[1]?.trim() || fallback;
};
